import { DEFAULT_NAMESPACE, groupRefOf, normalizeGroupRef } from './groupRef';

export const WORKS_ON_BEHALF_OF = 'worksOnBehalfOf';
export const WORKS_ON_BEHALF_OF_BY = 'worksOnBehalfOfBy';
export const INHERITS_WORKS_ON_BEHALF_OF = 'inheritsWorksOnBehalfOf';
export const INHERITS_WORKS_ON_BEHALF_OF_BY = 'inheritsWorksOnBehalfOfBy';

/** Shared contract: the processor's index TTL; the router waits past it before refreshing descendants. */
export const WOBO_GROUP_INDEX_TTL_MS = 30_000;

export interface WoboGroupLike {
  metadata: { name: string; namespace?: string };
  spec?: { parent?: unknown; worksOnBehalfOf?: unknown; type?: unknown };
}

export type WoboAnomaly =
  | { code: 'CYCLE'; at: string }
  | { code: 'DANGLING_PARENT'; at: string; parentRef: string }
  | { code: 'INVALID_PARENT'; at: string; raw: unknown }
  | { code: 'INVALID_WOBO_REF'; at: string; raw: unknown };

export type OwnWobo =
  | { kind: 'none' }
  | { kind: 'explicit'; targetRef: string }
  | { kind: 'invalid'; raw: unknown };

export interface EffectiveWobo {
  own: OwnWobo;
  /** Nearest ancestor's explicit WOBO, computed regardless of `own` (needed for the "Overrides inherited …" UI). */
  inherited?: { targetRef: string; fromRef: string };
  anomaly?: WoboAnomaly;
}

function isSet(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/** Same test the explicit processor applies to `spec.worksOnBehalfOf`. */
export function hasExplicitWobo(group: WoboGroupLike): boolean {
  return isSet(group.spec?.worksOnBehalfOf);
}

function namespaceOf(group: WoboGroupLike): string {
  return group.metadata.namespace?.trim() || DEFAULT_NAMESPACE;
}

export function resolveEffectiveWobo(
  group: WoboGroupLike,
  getGroup: (canonicalRef: string) => WoboGroupLike | undefined,
): EffectiveWobo {
  const selfRef = groupRefOf(group);

  let own: OwnWobo = { kind: 'none' };
  if (hasExplicitWobo(group)) {
    const targetRef = normalizeGroupRef(
      group.spec?.worksOnBehalfOf,
      namespaceOf(group),
    );
    own = targetRef
      ? { kind: 'explicit', targetRef }
      : { kind: 'invalid', raw: group.spec?.worksOnBehalfOf };
  }

  const result: EffectiveWobo = { own };

  const parentOf = (
    node: WoboGroupLike,
    at: string,
  ): { ref?: string; anomaly?: WoboAnomaly } => {
    const raw = node.spec?.parent;
    if (!isSet(raw)) {
      return {};
    }
    const ref = normalizeGroupRef(raw, namespaceOf(node));
    return ref ? { ref } : { anomaly: { code: 'INVALID_PARENT', at, raw } };
  };

  const visited = new Set<string>([selfRef]);
  let step = parentOf(group, selfRef);
  if (step.anomaly) {
    result.anomaly = step.anomaly;
    return result;
  }
  let parentRef = step.ref;

  while (parentRef) {
    if (visited.has(parentRef)) {
      result.anomaly = { code: 'CYCLE', at: parentRef };
      return result;
    }
    visited.add(parentRef);

    const node = getGroup(parentRef);
    if (!node) {
      result.anomaly = { code: 'DANGLING_PARENT', at: selfRef, parentRef };
      return result;
    }

    if (hasExplicitWobo(node)) {
      const targetRef = normalizeGroupRef(
        node.spec?.worksOnBehalfOf,
        namespaceOf(node),
      );
      if (targetRef) {
        if (targetRef === selfRef) {
          result.anomaly = { code: 'CYCLE', at: parentRef };
        } else {
          result.inherited = { targetRef, fromRef: parentRef };
        }
      } else {
        result.anomaly = {
          code: 'INVALID_WOBO_REF',
          at: parentRef,
          raw: node.spec?.worksOnBehalfOf,
        };
      }
      return result;
    }

    step = parentOf(node, parentRef);
    if (step.anomaly) {
      result.anomaly = step.anomaly;
      return result;
    }
    parentRef = step.ref;
  }

  return result;
}

/** own explicit target, else inherited; undefined for own 'invalid' (never falls back). */
export function effectiveWoboTargetOf(
  result: EffectiveWobo,
): string | undefined {
  if (result.own.kind === 'explicit') {
    return result.own.targetRef;
  }
  if (result.own.kind === 'invalid') {
    return undefined;
  }
  return result.inherited?.targetRef;
}
